/**
 * Calendar event → "what am I still missing?" cross-reference.
 *
 * The Profile Raid Radar already answers this for the one or two events on a
 * trainer's radar (raidDexCrossRef.raidDexGaps). The Calendar asks it for EVERY
 * event on the page, which needs two extra rules on top:
 *
 *   1. Availability. raidDexGaps derives its variants from the trainer's
 *      collection foci, so a lucky-hunter gets a "Lucky" gap flagged for every
 *      spawn — including species that cannot carry that variant in GO. Every gap
 *      here is filtered through the availability roster so the list only ever
 *      names copies the trainer could actually go and get.
 *   2. A cap. An event can feature dozens of species; the card shows a strip,
 *      not a matrix. The caller passes the cap so the UI decides how much room
 *      it has, and `total` reports the true count so the strip can say
 *      "+7 more" rather than silently truncating.
 *
 * Pure + storage-agnostic (the caller injects `getPresent` and `isAvailable`) so
 * it is unit-testable without a DOM or the network.
 */
import {
  capGapsByVariant,
  raidDexGaps,
  type RaidDexGap,
  type RaidDexVariant,
  type RaidSpawnInput,
} from "./raidDexCrossRef";
import type { CollectionFocus } from "./userPriorities";

export interface EventDexGapsResult {
  /** The gaps to render, capped at `limit`. */
  gaps: RaidDexGap[];
  /** How many gaps there are in total, before the cap. */
  total: number;
  /**
   * True when the event has at least one species we could cross-reference.
   * A "generic" feed event with no resolvable species is NOT the same as "you
   * already have everything" and the UI must not claim it is.
   */
  hasLinkedSpecies: boolean;
}

/**
 * Does this event name any Pokémon at all?
 *
 * A "generic" feed entry that nobody has linked species to yet cannot be
 * cross-referenced against anything, so every surface built on that comparison
 * has nothing to say about it. Shared so the surfaces AGREE about which events
 * those are, and so a card can decide not to render rather than rendering a
 * sentence explaining why it is empty.
 */
export function eventHasLinkedSpecies(spawns: readonly RaidSpawnInput[]): boolean {
  return spawns.some(
    (s) => typeof s.speciesId === "number" && Number.isFinite(s.speciesId) && s.speciesId >= 1,
  );
}

export function eventDexGaps(
  spawns: readonly RaidSpawnInput[],
  foci: readonly CollectionFocus[],
  getPresent: (variant: RaidDexVariant, speciesId: number, formId?: string) => boolean,
  isAvailable: (variant: RaidDexVariant, speciesId: number, formId?: string) => boolean,
  limit: number,
  opts?: {
    /** Max Battle / Max Monday event: featured species imply a Dynamax gap
     * check (see raidDexGaps). */
    impliedDynamax?: boolean;
  },
): EventDexGapsResult {
  const hasLinkedSpecies = eventHasLinkedSpecies(spawns);
  // Grouped by VARIANT, not by species (2026-08-07, user direction): raidDexGaps
  // emits in spawn order, which interleaves each species' main/shiny/lucky.
  //
  // The cap is shared round-robin ACROSS those groups rather than applied as a
  // flat slice (see capGapsByVariant). A flat slice dropped whole tail
  // variants, which on a nine-species Max Monday linked in Shiny + 100% +
  // D-MAX meant the D-MAX the event is named for never reached the card.
  const all = raidDexGaps(spawns, foci, getPresent, opts).filter((g) =>
    isAvailable(g.variant, g.speciesId, g.formId),
  );
  return {
    gaps: capGapsByVariant(all, limit),
    total: all.length,
    hasLinkedSpecies,
  };
}
